import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { Search, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { stageLabel } from "@/lib/stages";

const STAGE_TONE = { shortlist: "tp-chip-emerald", incomplete: "tp-chip-amber", rejected: "tp-chip-rose", invited: "" };

function useCandidateSearch(q) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/candidates/search", { params: { q: term } })
        .then((r) => setResults(r.data || [])).catch(() => setResults([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return { results, loading };
}

function ResultRow({ r, active, onPick, onHover }) {
  return (
    <button type="button" onMouseDown={(e) => { e.preventDefault(); onPick(r); }} onMouseEnter={onHover}
      data-testid={`search-result-${r.id}`}
      className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${active ? "bg-indigo-50" : "hover:bg-slate-50"}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-900 truncate">{r.name}</div>
        <div className="text-xs text-slate-500 truncate">{r.email}</div>
        <div className="sm:hidden text-xs text-indigo-700 truncate">{r.job_title}{r.client ? ` · ${r.client}` : ""}</div>
      </div>
      <div className="hidden sm:block min-w-0 max-w-[40%] text-xs text-slate-600 truncate text-right" data-testid={`search-result-job-${r.id}`}>
        {r.job_title}{r.client ? ` · ${r.client}` : ""}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className={`tp-chip ${STAGE_TONE[r.stage] ?? "tp-chip-indigo"} text-[10px]`}>{stageLabel(r.stage || "invited")}</span>
        {r.hired && <span className="tp-chip tp-chip-emerald text-[10px]">Hired</span>}
        <span className="w-7 text-right text-xs font-mono text-slate-700">{r.overall_score ?? "—"}</span>
      </div>
    </button>
  );
}

function SearchPanel({ autoFocus, onClose, inputRef }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const { results, loading } = useCandidateSearch(q);
  useEffect(() => { setIdx(0); }, [results]);
  const pick = (r) => { setQ(""); setOpen(false); onClose?.(); nav(`/app/candidates/${r.id}`); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && results[idx]) pick(results[idx]);
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); onClose?.(); }
  };
  const show = open && q.trim().length >= 2;
  return (
    <div className="relative w-full">
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100 transition-colors">
        {loading ? <Loader2 className="w-4 h-4 text-slate-400 animate-spin" /> : <Search className="w-4 h-4 text-slate-400" />}
        <input ref={inputRef} autoFocus={autoFocus} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={onKey}
          placeholder="Search candidates by name, email, phone…" data-testid="global-search-input"
          className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400" />
        <kbd className="hidden lg:inline text-[10px] font-mono text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">Ctrl K</kbd>
      </div>
      {show && (
        <div className="absolute left-0 right-0 mt-1.5 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {!loading && results.length === 0
            ? <div className="px-3 py-4 text-sm text-slate-500" data-testid="global-search-empty">No candidates match “{q.trim()}”</div>
            : results.map((r, i) => <ResultRow key={r.id} r={r} active={i === idx} onPick={pick} onHover={() => setIdx(i)} />)}
        </div>
      )}
    </div>
  );
}

export default function GlobalSearch() {
  const inputRef = useRef(null);
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (window.matchMedia("(min-width: 768px)").matches) inputRef.current?.focus(); else setMobile(true);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  return (
    <>
      <div className="hidden md:block flex-1 min-w-[320px] max-w-xl mx-4" data-testid="global-search"><SearchPanel inputRef={inputRef} /></div>
      <button type="button" className="md:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-700" onClick={() => setMobile(true)}
        data-testid="global-search-mobile-btn" aria-label="Search candidates"><Search className="w-5 h-5" /></button>
      {mobile && createPortal(
        <div className="fixed inset-0 z-[60] bg-white p-3 flex gap-2 items-start md:hidden" data-testid="global-search-mobile">
          <SearchPanel autoFocus inputRef={{ current: null }} onClose={() => setMobile(false)} />
          <button type="button" onClick={() => setMobile(false)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="global-search-mobile-close" aria-label="Close search"><X className="w-5 h-5" /></button>
        </div>, document.body
      )}
    </>
  );
}
